import React, { useEffect, useState, useCallback, useRef } from 'react';
import useIsMobile from '../../utils/useIsMobile';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = ['technical', 'product', 'process'];

// ── Product Sidebar ────────────────────────────────────────────────────────────
// onSelect(product, categoryId) — either or both may be set
function ProductSidebar({ selectedProduct, selectedCategoryId, onSelect }) {
  const [productCategories, setProductCategories] = useState([]);
  const [allProducts, setAllProducts]             = useState([]);

  useEffect(() => {
    apiFetch('/products/categories').then(d => setProductCategories(d || [])).catch(() => {});
    apiFetch('/products').then(p => setAllProducts(p || [])).catch(() => {});
  }, []);

  const filteredProducts = selectedCategoryId
    ? allProducts.filter(p => p.category_id === selectedCategoryId)
    : allProducts;

  function handleCategoryChange(catId) {
    onSelect(null, catId);
  }

  function handleProductChange(productId) {
    if (!productId) { onSelect(null, selectedCategoryId); return; }
    const product = allProducts.find(p => p.id === productId);
    onSelect(product || null, selectedCategoryId);
  }

  function handleClear() {
    onSelect(null, '');
  }

  const selectedCategoryName = productCategories.find(c => c.id === selectedCategoryId)?.name;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>
        Filter by Product
      </div>

      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="label" style={{ fontSize: 12 }}>Category</label>
        <select className="input" style={{ fontSize: 13 }} value={selectedCategoryId} onChange={e => handleCategoryChange(e.target.value)}>
          <option value="">All Categories</option>
          {productCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="label" style={{ fontSize: 12 }}>Product</label>
        <select className="input" style={{ fontSize: 13 }} value={selectedProduct?.id || ''} onChange={e => handleProductChange(e.target.value)}>
          <option value="">All Products{selectedCategoryId ? ' in category' : ''}</option>
          {filteredProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {(selectedCategoryId || selectedProduct) && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Filtering by</div>
          {selectedProduct
            ? <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--status-resolved-text)' }}>{selectedProduct.name}</div>
            : <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--status-resolved-text)' }}>{selectedCategoryName} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(all products)</span></div>
          }
          <button onClick={handleClear} style={{ marginTop: 6, background: 'none', border: 'none', fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}>✕ Clear filter</button>
        </div>
      )}
    </div>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
function CategoryPill({ value, active, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '4px 14px', borderRadius: 99, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: active ? '#0B6623' : 'var(--n-100)', color: active ? '#fff' : 'var(--text-secondary)', transition: 'all .15s', textTransform: 'capitalize' }}>
      {value === '' ? 'All' : value}
    </button>
  );
}

function SearchBar({ value, onChange, placeholder, inputRef }) {
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }}>🔍</span>
      <input ref={inputRef} className="input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ paddingLeft: 32, fontSize: 13 }} />
    </div>
  );
}

// ── Export / Import helpers ────────────────────────────────────────────────────
function downloadJSON(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// Authenticated download of a server file (template) via Bearer token.
function authedDownload(path, filename) {
  const token = sessionStorage.getItem('kgreen_token');
  fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error('Download failed'); return r.blob(); })
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(a.href);
    })
    .catch(e => alert(e.message));
}

// Template download button with an Excel default + a small CSV option.
function TemplateButton({ base, name }) {
  return (
    <span style={{ display: 'inline-flex' }}>
      <button onClick={() => authedDownload(`${base}/template`, `${name}_Template.xlsx`)}
        className="btn btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>⬇ Template (Excel)</button>
      <button onClick={() => authedDownload(`${base}/template?format=csv`, `${name}_Template.csv`)}
        className="btn btn-ghost" style={{ whiteSpace: 'nowrap', fontSize: 12 }} title="Download as CSV">CSV</button>
    </span>
  );
}

// Import button: accepts CSV / Excel (posted as a file) or JSON (posted as an array).
function ImportButton({ endpoint, onDone, label }) {
  const ref = React.useRef();
  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const token = sessionStorage.getItem('kgreen_token');
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    try {
      let res;
      if (ext === 'json') {
        const text = await file.text();
        const data = JSON.parse(text);
        res = await fetch(`/api${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(Array.isArray(data) ? data : [data]),
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        res = await fetch(`/api${endpoint}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      }
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Import failed');
      onDone(result);
    } catch (err) { alert('Import failed: ' + err.message); }
  }
  return (
    <>
      <input ref={ref} type="file" accept=".csv,.xlsx,.xls,.json" style={{ display: 'none' }} onChange={handleFile} />
      <button onClick={() => ref.current?.click()} className="btn btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>📥 {label}</button>
    </>
  );
}

// ── FAQ Tab ────────────────────────────────────────────────────────────────────
function FAQTab({ canEdit, canDelete, isAdmin, productId, productCategoryId }) {
  const [allItems, setAllItems]  = useState([]);
  const [search, setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [category, setCategory] = useState('');
  const [expanded, setExpanded] = useState(null);
  const searchRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({ question: '', answer: '', category: 'technical', product_id: productId || '', product_category_id: '', meta: '' });
  const [saving, setSaving]     = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);

  useEffect(() => {
    apiFetch('/products').then(p => setAllProducts(p || [])).catch(() => {});
    apiFetch('/products/categories').then(c => setProductCategories(c || [])).catch(() => {});
  }, []);

  // Debounce search 350ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset highlight when search or category changes
  useEffect(() => { setSelectedIdx(-1); }, [debouncedSearch, category]);

  // Reload from server only when product/category filter changes (not on search)
  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (productId)              params.set('product_id', productId);
    else if (productCategoryId) params.set('product_category_id', productCategoryId);
    const data = await apiFetch(`/knowledge/faq?${params}`).catch(() => []);
    setAllItems(data || []);
  }, [productId, productCategoryId]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter using debounced search
  const items = allItems.filter(item => {
    if (category && item.category !== category) return false;
    if (!debouncedSearch.trim()) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      item.question?.toLowerCase().includes(q) ||
      item.answer?.toLowerCase().includes(q) ||
      item.meta?.toLowerCase().includes(q) ||
      item.product_name?.toLowerCase().includes(q)
    );
  });

  // Keyboard navigation
  const handleKeyNav = useCallback((e) => {
    if (!items.length) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' && document.activeElement !== searchRef.current) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIdx >= 0 && items[selectedIdx]) {
        const id = items[selectedIdx].id;
        setExpanded(ex => ex === id ? null : id);
      }
    }
  }, [items, selectedIdx]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyNav);
    return () => window.removeEventListener('keydown', handleKeyNav);
  }, [handleKeyNav]);

  // Sync product_id into form when product filter changes
  useEffect(() => {
    setForm(f => ({ ...f, product_id: productId || '' }));
  }, [productId]);

  function openAdd() {
    setEditing(null);
    setForm({ question: '', answer: '', category: 'technical', product_id: productId || '', product_category_id: '', meta: '' });
    setShowForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    const prod = allProducts.find(p => p.id === item.product_id);
    setForm({ question: item.question, answer: item.answer, category: item.category, product_id: item.product_id || '', product_category_id: prod?.category_id || '', meta: item.meta || '' });
    setShowForm(true);
  }

  async function save() {
    if (!form.question.trim() || !form.answer.trim()) return;
    setSaving(true);
    try {
      const body = { ...form, product_id: form.product_id || null };
      if (editing) {
        const updated = await apiFetch(`/knowledge/faq/${editing.id}`, { method: 'PATCH', body });
        setAllItems(prev => prev.map(i => i.id === editing.id ? updated : i));
      } else {
        const created = await apiFetch('/knowledge/faq', { method: 'POST', body });
        setAllItems(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch (e) { alert(e.message); }
    setSaving(false);
  }

  async function del(id) {
    if (!window.confirm('Delete this FAQ?')) return;
    await apiFetch(`/knowledge/faq/${id}`, { method: 'DELETE' });
    setAllItems(prev => prev.filter(i => i.id !== id));
  }

  function handleExport() {
    const exportData = allItems.map(({ question, answer, category, product_id, product_name, meta }) =>
      ({ question, answer, category, product_id: product_id || null, product_name: product_name || null, meta: meta || null })
    );
    downloadJSON(exportData, `faq_export_${new Date().toISOString().slice(0,10)}.json`);
  }

  function handleImportDone(res) {
    const issues = [...(res.errors || []), ...(res.warnings || [])];
    const extra = issues.length ? `\n\nNotes:\n• ${issues.join('\n• ')}` : '';
    alert(`Import complete: ${res.inserted} added, ${res.skipped} skipped (duplicates or invalid).${extra}`);
    load();
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchBar value={search} onChange={v => { setSearch(v); setSelectedIdx(-1); }} placeholder="Search FAQs…" inputRef={searchRef} />
        {isAdmin && <TemplateButton base="/knowledge/faq" name="FAQ" />}
        {isAdmin && <ImportButton endpoint="/knowledge/faq/import" onDone={handleImportDone} label="Import / Append" />}
        {isAdmin && <button onClick={handleExport} className="btn btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>⬇ Export JSON</button>}
        {canEdit && <button onClick={openAdd} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>+ Add FAQ</button>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <CategoryPill value="" active={category === ''} onClick={() => setCategory('')} />
        {CATEGORIES.map(c => <CategoryPill key={c} value={c} active={category === c} onClick={() => setCategory(c)} />)}
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>❓</div>
          <div style={{ fontSize: 14 }}>No FAQs found.{canEdit ? ' Add the first one.' : ''}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, idx) => {
            const isSelected = idx === selectedIdx;
            return (
            <div key={item.id} onClick={() => setSelectedIdx(idx)}
              style={{ background: isSelected ? 'var(--action-bg)' : 'var(--bg-surface)', border: isSelected ? '1px solid #0B6623' : '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', outline: isSelected ? '2px solid #0B6623' : 'none', outlineOffset: -1 }}>
              <div onClick={() => setExpanded(expanded === item.id ? null : item.id)} style={{ padding: '13px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', background: 'var(--action-bg)', color: 'var(--status-resolved-text)', padding: '2px 8px', borderRadius: 99 }}>{item.category}</span>
                    {item.product_name && <span style={{ fontSize: 11, background: 'var(--blue-50)', color: 'var(--blue-700)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>{item.product_name}</span>}
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>Q: {item.question}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {canEdit && <button onClick={e => { e.stopPropagation(); openEdit(item); }} style={{ background: 'var(--n-100)', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text-muted)', fontWeight: 600 }}>Edit</button>}
                    {canDelete && <button onClick={e => { e.stopPropagation(); del(item.id); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>Del</button>}
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: 14 }}>{expanded === item.id ? '▲' : '▼'}</span>
                </div>
              </div>
              {expanded === item.id && (
                <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--n-100)' }}>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.75, margin: '12px 0 0', whiteSpace: 'pre-wrap' }}>A: {item.answer}</p>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 560, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700 }}>{editing ? 'Edit FAQ' : 'Add FAQ'}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="label">FAQ Category</label>
                <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Product Category</label>
                <select className="input" value={form.product_category_id} onChange={e => setForm(f => ({ ...f, product_category_id: e.target.value, product_id: '' }))}>
                  <option value="">— All Categories —</option>
                  {productCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Product</label>
                <select className="input" value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}>
                  <option value="">— General (no product) —</option>
                  {(form.product_category_id ? allProducts.filter(p => p.category_id === form.product_category_id) : allProducts).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="label">Question *</label>
              <input className="input" value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} placeholder="Type the question…" />
            </div>
            <div className="form-group">
              <label className="label">Answer *</label>
              <textarea className="input" rows={5} value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} placeholder="Type the answer…" style={{ resize: 'vertical' }} />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="label">Meta Keywords <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — helps with search)</span></label>
              <input className="input" value={form.meta} onChange={e => setForm(f => ({ ...f, meta: e.target.value }))} placeholder="e.g. error, installation, network, reset" />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 0 }}>Separate keywords with commas. Users searching these terms will find this FAQ.</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.question.trim() || !form.answer.trim()}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Knowledge Tab ──────────────────────────────────────────────────────────────
function KnowledgeTab({ canEdit, canDelete, isAdmin, productId, productCategoryId }) {
  const isMobile = useIsMobile();
  const [allItems, setAllItems]  = useState([]);
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('');
  const [viewing, setViewing]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({ title: '', content: '', category: 'technical', tags: '', product_id: '', link_url: '', meta: '' });
  const [saving, setSaving]     = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    apiFetch('/products').then(p => setAllProducts(p || [])).catch(() => {});
  }, []);

  // Reload from server only when product/category filter changes (not on search)
  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (productId)              params.set('product_id', productId);
    else if (productCategoryId) params.set('product_category_id', productCategoryId);
    const data = await apiFetch(`/knowledge/articles?${params}`).catch(() => []);
    setAllItems(data || []);
  }, [productId, productCategoryId]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter for instant search
  const items = allItems.filter(item => {
    if (category && item.category !== category) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const tags = (() => { try { return JSON.parse(item.tags || '[]').join(' '); } catch { return ''; } })();
    return (
      item.title?.toLowerCase().includes(q) ||
      item.content?.toLowerCase().includes(q) ||
      item.meta?.toLowerCase().includes(q) ||
      item.product_name?.toLowerCase().includes(q) ||
      tags.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    setForm(f => ({ ...f, product_id: productId || '' }));
  }, [productId]);

  function openAdd() {
    setEditing(null);
    setForm({ title: '', content: '', category: 'technical', tags: '', product_id: productId || '', link_url: '', meta: '' });
    setShowForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    const tags = (() => { try { return JSON.parse(item.tags || '[]').join(', '); } catch { return ''; } })();
    setForm({ title: item.title, content: item.content, category: item.category, tags, product_id: item.product_id || '', link_url: item.link_url || '', meta: item.meta || '' });
    setShowForm(true);
  }

  async function save() {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
    try {
      const body = { title: form.title, content: form.content, category: form.category, tags, product_id: form.product_id || null, link_url: form.link_url || null, meta: form.meta || null };
      if (editing) {
        const updated = await apiFetch(`/knowledge/articles/${editing.id}`, { method: 'PATCH', body });
        setAllItems(prev => prev.map(i => i.id === editing.id ? updated : i));
        if (viewing?.id === editing.id) setViewing(updated);
      } else {
        const created = await apiFetch('/knowledge/articles', { method: 'POST', body });
        setAllItems(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch (e) { alert(e.message); }
    setSaving(false);
  }

  async function del(id) {
    if (!window.confirm('Delete this article?')) return;
    await apiFetch(`/knowledge/articles/${id}`, { method: 'DELETE' });
    setAllItems(prev => prev.filter(i => i.id !== id));
    if (viewing?.id === id) setViewing(null);
  }

  const parseTags = raw => { try { return JSON.parse(raw || '[]'); } catch { return []; } };

  function handleExport() {
    const exportData = allItems.map(item => ({
      title: item.title,
      content: item.content,
      category: item.category,
      tags: parseTags(item.tags),
      product_id: item.product_id || null,
      product_name: item.product_name || null,
      link_url: item.link_url || null,
      meta: item.meta || null,
    }));
    downloadJSON(exportData, `knowledge_export_${new Date().toISOString().slice(0,10)}.json`);
  }

  function handleImportDone(res) {
    const issues = [...(res.errors || []), ...(res.warnings || [])];
    const extra = issues.length ? `\n\nNotes:\n• ${issues.join('\n• ')}` : '';
    alert(`Import complete: ${res.inserted} added, ${res.skipped} skipped (duplicates or invalid).${extra}`);
    load();
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search articles…" />
        {isAdmin && <TemplateButton base="/knowledge/articles" name="Knowledge" />}
        {isAdmin && <ImportButton endpoint="/knowledge/articles/import" onDone={handleImportDone} label="Import / Append" />}
        {isAdmin && <button onClick={handleExport} className="btn btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>⬇ Export JSON</button>}
        {canEdit && <button onClick={openAdd} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>+ New Article</button>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <CategoryPill value="" active={category === ''} onClick={() => setCategory('')} />
        {CATEGORIES.map(c => <CategoryPill key={c} value={c} active={category === c} onClick={() => setCategory(c)} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: (viewing && !isMobile) ? '1fr 1.5fr' : '1fr', gap: isMobile ? 14 : 20 }}>
        <div>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📚</div>
              <div style={{ fontSize: 14 }}>No articles found.{canEdit ? ' Create the first one.' : ''}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(item => {
                const tags = parseTags(item.tags);
                const isActive = viewing?.id === item.id;
                return (
                  <div key={item.id} onClick={() => setViewing(isActive ? null : item)} style={{ background: isActive ? 'var(--action-bg)' : 'var(--bg-surface)', border: `1px solid ${isActive ? 'var(--action-hover)' : 'var(--border-subtle)'}`, borderRadius: 10, padding: '13px 16px', cursor: 'pointer', transition: 'all .15s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', background: 'var(--blue-50)', color: 'var(--blue-700)', padding: '2px 8px', borderRadius: 99 }}>{item.category}</span>
                          {item.product_name && <span style={{ fontSize: 11, background: 'var(--action-bg)', color: 'var(--status-resolved-text)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>{item.product_name}</span>}
                          {tags.map(t => <span key={t} style={{ fontSize: 10, background: 'var(--n-100)', color: 'var(--text-secondary)', padding: '2px 7px', borderRadius: 99 }}>#{t}</span>)}
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-main)', margin: '0 0 3px' }}>{item.title}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>By {item.created_by_name} · {new Date(item.created_at).toLocaleDateString()}</p>
                          {item.link_url && <a href={item.link_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: 'var(--blue-500)', fontWeight: 600, textDecoration: 'none' }}>🔗 Reference</a>}
                        </div>
                      </div>
                      {(canEdit || canDelete) && (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          {canEdit && <button onClick={() => openEdit(item)} style={{ background: 'var(--n-100)', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text-muted)', fontWeight: 600 }}>Edit</button>}
                          {canDelete && <button onClick={() => del(item.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>Del</button>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {viewing && (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: isMobile ? 16 : 24, position: isMobile ? 'static' : 'sticky', top: 16, maxHeight: isMobile ? 'none' : '75vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', background: 'var(--blue-50)', color: 'var(--blue-700)', padding: '2px 8px', borderRadius: 99 }}>{viewing.category}</span>
                  {viewing.product_name && <span style={{ fontSize: 11, background: 'var(--action-bg)', color: 'var(--status-resolved-text)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>{viewing.product_name}</span>}
                  {parseTags(viewing.tags).map(t => <span key={t} style={{ fontSize: 10, background: 'var(--n-100)', color: 'var(--text-secondary)', padding: '2px 7px', borderRadius: 99 }}>#{t}</span>)}
                </div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' }}>{viewing.title}</h2>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>By {viewing.created_by_name} · {new Date(viewing.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setViewing(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}>×</button>
            </div>
            {viewing.link_url && (
              <div style={{ margin: '0 0 10px', padding: '10px 14px', background: 'var(--blue-50)', border: '1px solid #bfdbfe', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13 }}>🔗</span>
                <a href={viewing.link_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--blue-700)', fontWeight: 600, wordBreak: 'break-all' }}>{viewing.link_url}</a>
              </div>
            )}
            {viewing.meta && (
              <div style={{ margin: '0 0 14px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>Keywords: </span>
                {viewing.meta.split(',').map(k => k.trim()).filter(Boolean).map(k => (
                  <span key={k} style={{ display: 'inline-block', fontSize: 11, background: 'var(--n-100)', color: 'var(--text-muted)', borderRadius: 99, padding: '2px 8px', marginRight: 4, marginBottom: 4 }}>{k}</span>
                ))}
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--n-100)', paddingTop: 16, fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {viewing.content}
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 640, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700 }}>{editing ? 'Edit Article' : 'New Article'}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>
            <div className="form-group">
              <label className="label">Title *</label>
              <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Article title…" autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="label">Category *</label>
                <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Product <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                <select className="input" value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}>
                  <option value="">— No product —</option>
                  {allProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="label">Reference Link <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input className="input" type="url" value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="https://…" />
            </div>
            <div className="form-group">
              <label className="label">Tags <span style={{ color: '#94a3b8', fontWeight: 400 }}>(comma separated, optional)</span></label>
              <input className="input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. setup, config, error" />
            </div>
            <div className="form-group">
              <label className="label">Meta Keywords <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — helps with search)</span></label>
              <input className="input" value={form.meta} onChange={e => setForm(f => ({ ...f, meta: e.target.value }))} placeholder="e.g. troubleshoot, install, vpn, timeout" />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 0 }}>Separate keywords with commas. Users searching these terms will find this article.</p>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="label">Content *</label>
              <textarea className="input" rows={10} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Write the article content here…" style={{ resize: 'vertical', lineHeight: 1.7 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.title.trim() || !form.content.trim()}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pending FAQ Approvals Tab (admin only) ─────────────────────────────────────
function PendingFAQTab() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const data = await apiFetch('/knowledge/faq/pending').catch(() => []);
    setItems(data || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function approve(id) {
    await apiFetch(`/knowledge/faq/${id}/approve`, { method: 'POST' });
    setItems(prev => prev.filter(i => i.id !== id));
  }

  async function toArticle(id) {
    await apiFetch(`/knowledge/faq/${id}/to-article`, { method: 'POST' });
    setItems(prev => prev.filter(i => i.id !== id));
  }

  async function reject(id) {
    if (!window.confirm('Reject and delete this FAQ submission?')) return;
    await apiFetch(`/knowledge/faq/${id}/reject`, { method: 'POST' });
    setItems(prev => prev.filter(i => i.id !== id));
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>;

  if (items.length === 0) return (
    <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
      <p>No pending FAQ submissions. All clear!</p>
    </div>
  );

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        {items.length} submission{items.length !== 1 ? 's' : ''} waiting for your review. Approved entries will appear in the live FAQ immediately.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {items.map(item => (
          <div key={item.id} className="card" style={{ padding: 20, borderLeft: '4px solid #f59e0b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Submitted by <strong>{item.submitted_by_name || 'Agent'}</strong>
                  {item.ticket_id && <> · Ticket ID: <code style={{ fontSize: 11 }}>{item.ticket_id.slice(0, 8)}…</code></>}
                  {item.created_at && <> · {new Date(item.created_at + 'Z').toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: 'var(--text-main)' }}>Q: {item.question}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>A: {item.answer}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-primary btn-sm" style={{ background: '#0d7a28', whiteSpace: 'nowrap' }} onClick={() => approve(item.id)}>✓ Add to FAQ</button>
                <button className="btn btn-primary btn-sm" style={{ background: 'var(--blue-700)', whiteSpace: 'nowrap' }} onClick={() => toArticle(item.id)}>📚 Add to Knowledge Base</button>
                <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c', whiteSpace: 'nowrap' }} onClick={() => reject(item.id)}>✕ Reject</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function KnowledgeManagement() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [tab, setTab]                         = useState('faq');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [pendingCount, setPendingCount]       = useState(0);
  const canEdit   = ['admin', 'super_admin', 'manager'].includes(user?.role);
  const canDelete = ['admin', 'super_admin'].includes(user?.role);
  const isAdmin   = ['admin', 'super_admin'].includes(user?.role);

  useEffect(() => {
    if (isAdmin) {
      apiFetch('/knowledge/faq/pending').then(d => setPendingCount((d || []).length)).catch(() => {});
    }
  }, [isAdmin]);

  function handleSidebarSelect(product, categoryId) {
    setSelectedProduct(product);
    setSelectedCategoryId(categoryId !== undefined ? categoryId : selectedCategoryId);
  }

  return (
    <div style={{ padding: isMobile ? '14px 10px 40px' : '32px 28px 48px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">Knowledge Management</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>FAQ and knowledge base for the support team</p>
      </div>

      <div style={{ display: 'flex', gap: isMobile ? 14 : 24, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Left sidebar — product filter */}
        <div style={{ flexShrink: 0, width: isMobile ? '100%' : 220 }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 16 }}>
            <ProductSidebar
              selectedProduct={selectedProduct}
              selectedCategoryId={selectedCategoryId}
              onSelect={handleSidebarSelect}
            />
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
            {[
              { id: 'faq', label: '❓ FAQ' },
              { id: 'knowledge', label: '📚 Knowledge Base' },
              ...(isAdmin ? [{ id: 'pending', label: '🕐 Pending Approvals', badge: pendingCount }] : []),
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: tab === t.id ? '#0B6623' : 'var(--text-secondary)', borderBottom: `3px solid ${tab === t.id ? 'var(--status-resolved-text)' : 'transparent'}`, marginBottom: -2, transition: 'all .15s' }}>
                {t.label}
                {t.badge > 0 && (
                  <span style={{ background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 99, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{t.badge}</span>
                )}
              </button>
            ))}
          </div>

          {tab === 'faq'       && <FAQTab       canEdit={canEdit} canDelete={canDelete} isAdmin={isAdmin} productId={selectedProduct?.id || ''} productCategoryId={selectedProduct ? '' : selectedCategoryId} />}
          {tab === 'knowledge' && <KnowledgeTab canEdit={canEdit} canDelete={canDelete} isAdmin={isAdmin} productId={selectedProduct?.id || ''} productCategoryId={selectedProduct ? '' : selectedCategoryId} />}
          {tab === 'pending'   && isAdmin && <PendingFAQTab />}
        </div>
      </div>
    </div>
  );
}
